import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Trash2,
  Download,
  AlertTriangle,
  Shield,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  FileText,
  BarChart3,
  Brain,
  MessageSquare,
  HelpCircle,
  RefreshCw,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  ArrowUpRight,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import { riskExposureService } from '../../services/riskExposureService';

// Custom Dropdown Component for Risk Actions
const RiskActionDropdown = ({ risk, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 200;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (risk && (risk.id || risk._id)) {
      onView(risk);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (risk && (risk.id || risk._id)) {
      onEdit(risk);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    if (risk && (risk.id || risk._id)) {
      onDelete(risk);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={(e) => handleToggle(e)}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen && (
        <ul className="dropdown-menu dropdown-menu-end show" style={{
          position: 'absolute',
          [openUpward ? 'bottom' : 'top']: '100%',
          right: '0',
          zIndex: 9999,
          minWidth: '180px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '4px 0',
          backgroundColor: 'white',
          listStyle: 'none',
          margin: '4px 0 0 0',
          overflow: 'hidden'
        }}>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleView}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Eye
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              View Risk
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleEdit}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Edit
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Edit Risk
            </button>
          </li>
          <li
            style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '4px',
              paddingTop: '4px',
            }}
          >
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#dc2626',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2
                size={16}
                style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }}
              />
              Delete Risk
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const RiskExposure = () => {
  const { success, error: showError, toasts, removeToast } = useToast();

  const [riskData, setRiskData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [riskReports, setRiskReports] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const [showMitigationModal, setShowMitigationModal] = useState(false);
  const [showMitigationViewModal, setShowMitigationViewModal] = useState(false);
  const [selectedMitigation, setSelectedMitigation] = useState(null);
  const [mitigationFormData, setMitigationFormData] = useState({
    riskId: '',
    action: '',
    dueDate: '',
    status: 'Planned',
  });

  // Risk statistics
  const riskStats = {
    totalRisks: riskData.length,
    criticalRisks: riskData.filter((risk) => risk.severity === 'Critical')
      .length,
    highRisks: riskData.filter((risk) => risk.severity === 'High').length,
    openRisks: riskData.filter((risk) => risk.status === 'Open').length,
    mitigatedRisks: riskData.filter((risk) => risk.status === 'Mitigated')
      .length,
    averageRiskScore: riskData.length
      ? Math.round(
        riskData.reduce((sum, risk) => sum + (risk.riskScore || 0), 0) /
        riskData.length
      )
      : 0,
  };
  const mitigationPlans = riskData.flatMap((risk) =>
    (risk.mitigationPlans || []).map((plan, index) => ({
      ...plan,
      id: plan._id || `${risk._id}-plan-${index}`,
      riskTitle: risk.title,
      riskId: risk.riskId,
    }))
  );

  const loadRisks = async () => {
    try {
      setLoading(true);
      const response = await riskExposureService.getRisks();
      if (response.success) {
        setRiskData(response.data?.risks || response.data || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load risks');
    } finally {
      setLoading(false);
    }
  };

  const loadRiskReports = async () => {
    try {
      const response = await riskExposureService.getRiskReports({
        reportType: 'severity',
      });
      if (response.success) {
        const reportsWithKeys = (response.data || []).map(report => ({
          ...report,
          id: report.category
        }));
        setRiskReports(reportsWithKeys);
      }
    } catch (error) {
      showError(error.message || 'Failed to load risk reports');
    }
  };

  const loadAiInsights = async () => {
    try {
      const response = await riskExposureService.generateInsights({});
      if (response.success) {
        setAiInsights(response.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to load AI insights');
    }
  };

  useEffect(() => {
    loadRisks();
  }, []);

  useEffect(() => {
    if (activeTab === 'reports') {
      loadRiskReports();
    }
    if (activeTab === 'ai-insights') {
      loadAiInsights();
    }
  }, [activeTab]);

  // Define columns for DataTable
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'riskId',
      label: 'Risk ID',
      width: '140px',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ fontSize: '12px' }}>{value}</strong>
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Risk Title',
      width: '220px',
    },
    {
      key: 'riskType',
      label: 'Type',
      width: '120px',
    },
    {
      key: 'severity',
      label: 'Severity',
      width: '120px',
      render: (severity) => (
        <span
          className={
            'badge badge-sm ' +
            (severity === 'Critical'
              ? 'bg-danger'
              : severity === 'High'
                ? 'bg-warning'
                : severity === 'Medium'
                  ? 'bg-info'
                  : 'bg-success')
          }
          style={{ fontSize: '11px' }}
        >
          {severity}
        </span>
      ),
    },
    {
      key: 'probability',
      label: 'Probability',
      width: '120px',
      render: (value) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{value}%</span>
      ),
    },
    {
      key: 'impact',
      label: 'Impact',
      width: '120px',
      render: (value) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{value}%</span>
      ),
    },
    {
      key: 'riskScore',
      label: 'Risk Score',
      width: '120px',
      render: (value) => (
        <span style={{ fontWeight: '600', color: '#374151' }}>{value}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span
          className={
            'badge badge-sm ' +
            (status === 'Open'
              ? 'bg-warning'
              : status === 'Mitigated'
                ? 'bg-success'
                : 'bg-secondary')
          }
          style={{ fontSize: '11px' }}
        >
          {status}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <RiskActionDropdown
          risk={row}
          onView={handleViewRisk}
          onEdit={handleEditRisk}
          onDelete={handleDeleteRisk}
        />
      ),
    },
  ];

  const mitigationColumns = [
    {
      key: 'riskTitle',
      label: 'Risk',
      width: '220px',
    },
    {
      key: 'action',
      label: 'Mitigation Action',
      width: '260px',
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '140px',
      render: (value) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {value ? new Date(value).toLocaleDateString() : '—'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleViewMitigation(row)}
            style={{ padding: '4px 8px', fontSize: '12px' }}
            title="View Mitigation Plan"
          >
            <Eye size={14} />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleEditMitigation(row)}
            style={{ padding: '4px 8px', fontSize: '12px' }}
            title="Edit Mitigation Plan"
          >
            <Edit size={14} />
          </button>
        </div>
      )
    }
  ];

  const reportColumns = [
    {
      key: 'category',
      label: 'Category',
      width: '200px',
    },
    {
      key: 'count',
      label: 'Count',
      width: '120px',
    },
    {
      key: 'averageScore',
      label: 'Avg Risk Score',
      width: '160px',
    },
  ];

  const handleEditRisk = (risk) => {
    navigate(`/finance/risk/edit/${risk.id || risk._id}`);
  };

  const handleViewRisk = (risk) => {
    setSelectedRisk(risk);
    setShowViewModal(true);
  };

  const handleViewMitigation = (mitigation) => {
    setSelectedMitigation(mitigation);
    setShowMitigationViewModal(true);
  };

  const handleEditMitigation = (mitigation) => {
    setSelectedMitigation(mitigation);
    setMitigationFormData({
      riskId: mitigation.riskId || '',
      action: mitigation.action || '',
      dueDate: mitigation.dueDate ? new Date(mitigation.dueDate).toISOString().split('T')[0] : '',
      status: mitigation.status || 'Pending'
    });
    setShowMitigationModal(true);
  };

  const handleDeleteRisk = (risk) => {
    setRiskToDelete(risk);
    setShowDeleteModal(true);
  };

  // Confirm delete risk
  const confirmDeleteRisk = async () => {
    if (!riskToDelete) return;

    setIsDeleting(true);

    try {
      await riskExposureService.deleteRisk(riskToDelete._id);
      setRiskData((prev) =>
        prev.filter((risk) => risk._id !== riskToDelete._id)
      );
      success('Risk deleted successfully!');

      setShowDeleteModal(false);
      setRiskToDelete(null);
    } catch (error) {
      showError(error.message || 'Failed to delete risk');
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRiskToDelete(null);
    setIsDeleting(false);
  };


  const handleMitigationSubmit = async (event) => {
    event.preventDefault();
    if (!mitigationFormData.riskId) {
      showError('Select a risk before adding a mitigation plan');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        riskId: mitigationFormData.riskId,
        plan: {
          action: mitigationFormData.action.trim(),
          dueDate: mitigationFormData.dueDate || undefined,
          status: mitigationFormData.status,
        },
      };
      const response = await riskExposureService.createMitigationPlan(payload);
      if (response.success) {
        success('Mitigation plan added');
      }
      setShowMitigationModal(false);
      await loadRisks();
    } catch (error) {
      showError(error.message || 'Failed to add mitigation plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRowClick = (risk) => {
    setSelectedRisk(risk);
    setShowViewModal(true);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadRisks();
    if (activeTab === 'reports') {
      await loadRiskReports();
    }
    if (activeTab === 'ai-insights') {
      await loadAiInsights();
    }
    setLoading(false);
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/risk-exposure';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleNewRisk = () => {
    navigate('/finance/risk/create');
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    {
      label: 'New Risk',
      icon: <Plus size={18} />,
      onClick: handleNewRisk,
      variant: 'primary',
      tooltip: 'Create new risk entry',
    },
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Risk & Exposure"
        description="Monitor and manage financial risks and exposures"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {/* Total Risks */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +{riskStats.totalRisks}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <AlertTriangle size={20} style={{ color: 'var(--gold-primary)' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {riskStats.totalRisks}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Risks
                </div>
              </div>
            </div>
          </div>

          {/* High / Critical Risks */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#FEE2E2] text-[#991B1B]">
                Critical
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <TrendingUp size={20} style={{ color: 'var(--gold-primary)' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-red-600">
                  {riskStats.criticalRisks + riskStats.highRisks}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  High / Critical Risks
                </div>
              </div>
            </div>
          </div>

          {/* Mitigated Risks */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +{riskStats.mitigatedRisks}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Shield size={20} style={{ color: 'var(--gold-primary)' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-emerald-600">
                  {riskStats.mitigatedRisks}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Mitigated Risks
                </div>
              </div>
            </div>
          </div>

          {/* Average Risk Score */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-red-600">
                <TrendingUp size={14} /> Monitor
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: 'var(--gold-primary)' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {riskStats.averageRiskScore}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Avg Risk Score
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div style={{ marginBottom: '32px' }}>
          <div className="flex bg-white border border-slate-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`
        flex items-center justify-center gap-2 px-5 py-3 w-full
        text-sm font-medium
        border-b-2 transition-colors duration-200
        ${activeTab === 'overview'
                  ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                  : 'border-transparent text-slate-500 hover:bg-slate-100'
                }
      `}
            >
              <BarChart3
                size={18}
                className={
                  activeTab === 'overview'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-400'
                }
              />
              Risk Overview
            </button>

            <button
              onClick={() => setActiveTab('assessment')}
              className={`
        flex items-center justify-center gap-2 px-5 py-3 w-full
        text-sm font-medium
        border-b-2 transition-colors duration-200
        ${activeTab === 'assessment'
                  ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                  : 'border-transparent text-slate-500 hover:bg-slate-100'
                }
      `}
            >
              <AlertTriangle
                size={18}
                className={
                  activeTab === 'assessment'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-400'
                }
              />
              Risk Assessment
            </button>

            <button
              onClick={() => setActiveTab('mitigation')}
              className={`
        flex items-center justify-center gap-2 px-5 py-3 w-full
        text-sm font-medium
        border-b-2 transition-colors duration-200
        ${activeTab === 'mitigation'
                  ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                  : 'border-transparent text-slate-500 hover:bg-slate-100'
                }
      `}
            >
              <Shield
                size={18}
                className={
                  activeTab === 'mitigation'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-400'
                }
              />
              Mitigation Plans
            </button>

            <button
              onClick={() => setActiveTab('reports')}
              className={`
        flex items-center justify-center gap-2 px-5 py-3 w-full
        text-sm font-medium
        border-b-2 transition-colors duration-200
        ${activeTab === 'reports'
                  ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                  : 'border-transparent text-slate-500 hover:bg-slate-100'
                }
      `}
            >
              <FileText
                size={18}
                className={
                  activeTab === 'reports'
                    ? 'text-[var(--primary-color)]'
                    : 'text-slate-400'
                }
              />
              Risk Reports
            </button>

            <button
              onClick={() => setActiveTab('ai-insights')}
              className={`
        flex items-center justify-center gap-2 px-5 py-3 w-full
        text-sm font-medium
        border-b-2 transition-colors duration-200
        ${activeTab === 'ai-insights'
                  ? 'border-[#8B5CF6] text-[#8B5CF6] bg-white'
                  : 'border-transparent text-slate-500 hover:bg-slate-100'
                }
      `}
            >
              <Brain
                size={18}
                className={
                  activeTab === 'ai-insights'
                    ? 'text-[#8B5CF6]'
                    : 'text-slate-400'
                }
              />
              AI Insights
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-12" style={{ overflowX: 'hidden' }}>
            {activeTab === 'overview' && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <DataTable
                  data={riskData}
                  columns={columns}
                  title="Risk & Exposure Overview"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={false}
                  showCheckboxes={false}
                  onRowClick={handleRowClick}
                  searchPlaceholder="Search risks by title, type, or ID..."
                  loading={loading}
                  emptyMessage="No risks found. Click 'Add Risk' to get started."
                  className="shadow-lg"
                />
              </div>
            )}

            {activeTab === 'assessment' && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <DataTable
                  data={riskData}
                  columns={columns}
                  title="Risk Assessments"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={false}
                  showCheckboxes={false}
                  onRowClick={handleRowClick}
                  searchPlaceholder="Search risks by title or type..."
                  loading={loading}
                  emptyMessage="No risk assessments found. Click 'New Risk' to get started."
                  className="shadow-lg"
                />
              </div>
            )}

            {activeTab === 'mitigation' && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '12px',
                  }}
                >
                  <button
                    className="btn"
                    style={{
                      background: 'var(--primary-color)',
                      border: 'none',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}
                    onClick={() => setShowMitigationModal(true)}
                  >
                    <Shield size={16} className="me-2" />
                    Create Mitigation Plan
                  </button>
                </div>
                <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <DataTable
                    data={mitigationPlans}
                    columns={mitigationColumns}
                    title="Risk Mitigation Plans"
                    searchable={false}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={false}
                    showCheckboxes={false}
                    loading={loading}
                    emptyMessage="No mitigation plans found."
                  />
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <DataTable
                  data={riskReports}
                  columns={reportColumns}
                  title="Risk Reports by Severity"
                  searchable={false}
                  sortable={true}
                  exportable={true}
                  pagination={false}
                  showActions={false}
                  showCheckboxes={false}
                  loading={loading}
                  emptyMessage="No risk reports available."
                />
              </div>
            )}

            {activeTab === 'ai-insights' && (
              <div
                className="card"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                  className="card-header"
                  style={{
                    background:
                      'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
                    color: 'white',
                    borderRadius: '16px 16px 0 0',
                    border: 'none',
                    padding: '20px 24px',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <Brain size={20} />
                    AI-Powered Risk Insights
                  </h5>
                </div>
                <div className="card-body p-4">
                  {aiInsights ? (
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div>
                        <h6 style={{ fontWeight: 600 }}>Severity Summary</h6>
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: '18px',
                            color: '#4b5563',
                          }}
                        >
                          {aiInsights.summary?.map((item) => (
                            <li key={item.category}>
                              {item.category}: {item.count} risks, avg score{' '}
                              {item.averageScore}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h6 style={{ fontWeight: 600 }}>Recommendations</h6>
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: '18px',
                            color: '#4b5563',
                          }}
                        >
                          {aiInsights.recommendations?.map((rec, index) => (
                            <li key={`${rec.severity}-${index}`}>
                              {rec.severity}: {rec.action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#6b7280' }}>
                      No AI insights available.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>



        {showMitigationModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowMitigationModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                Add Mitigation Plan
              </h3>

              <form onSubmit={handleMitigationSubmit}>
                <div style={{ display: 'grid', gap: '12px' }}>

                  {/* Risk Select */}
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Risk
                    <select
                      value={mitigationFormData.riskId}
                      onChange={(event) =>
                        setMitigationFormData({
                          ...mitigationFormData,
                          riskId: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <option value="">Select risk</option>
                      {riskData.map((risk) => (
                        <option key={risk._id} value={risk._id}>
                          {risk.title}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Action */}
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Action
                    <input
                      type="text"
                      value={mitigationFormData.action}
                      onChange={(event) =>
                        setMitigationFormData({
                          ...mitigationFormData,
                          action: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>

                  {/* Due Date */}
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Due Date
                    <input
                      type="date"
                      value={mitigationFormData.dueDate}
                      onChange={(event) =>
                        setMitigationFormData({
                          ...mitigationFormData,
                          dueDate: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>

                  {/* Status */}
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Status
                    <input
                      type="text"
                      value={mitigationFormData.status}
                      onChange={(event) =>
                        setMitigationFormData({
                          ...mitigationFormData,
                          status: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                </div>

                {/* Footer Buttons */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    marginTop: '18px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowMitigationModal(false)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                      cursor: 'pointer',
                    }}
                  >
                    Save Plan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Risk Modal */}
        {showViewModal && selectedRisk && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000, // Higher than edit modal
              padding: '20px',
            }}
            onClick={() => { setShowViewModal(false); setSelectedRisk(null); }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
            >
              <div style={{
                padding: '16px 24px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Eye size={20} /> Risk Details
                </h5>
                <button
                  onClick={() => { setShowViewModal(false); setSelectedRisk(null); }}
                  style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}
                >×</button>
              </div>
              <div className="p-4">
                <RiskViewDetails risk={selectedRisk} />
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDeleteRisk}
          title="Delete Risk"
          message={`Are you sure you want to delete "${riskToDelete?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Mitigation Plan View Modal */}
        {showMitigationViewModal && selectedMitigation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
            onClick={() => {
              setShowMitigationViewModal(false);
              setSelectedMitigation(null);
            }}
          >
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              padding: '24px'
            }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: 0 }}>View Mitigation Plan</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowMitigationViewModal(false);
                    setSelectedMitigation(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  <XCircle size={20} />
                </button>
              </div>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Risk</label>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{selectedMitigation.riskTitle || '—'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Mitigation Action</label>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{selectedMitigation.action || '—'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Status</label>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>{selectedMitigation.status || '—'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Due Date</label>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>
                    {selectedMitigation.dueDate ? new Date(selectedMitigation.dueDate).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowMitigationViewModal(false);
                    handleEditMitigation(selectedMitigation);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4678be',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMitigationViewModal(false);
                    setSelectedMitigation(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

// Risk View Details Component
const RiskViewDetails = ({ risk }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ fontFamily: 'var(--font-family)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Top Row */}
        <div style={{ display: 'flex', gap: '16px', minHeight: '200px' }}>
          {/* Risk Overview */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card h-100">
              <div
                className="card-header py-2"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  📋 Risk Overview
                </h6>
              </div>
              <div className="card-body py-3">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Risk ID
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {risk?.riskId || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Title
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {risk?.title || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Risk Type
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {risk?.riskType || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Severity
                    </div>
                    <div>
                      <span
                        className={`badge badge-sm ${risk?.severity === 'Critical'
                            ? 'bg-danger'
                            : risk?.severity === 'High'
                              ? 'bg-warning'
                              : risk?.severity === 'Medium'
                                ? 'bg-info'
                                : 'bg-success'
                          }`}
                        style={{ fontSize: '11px' }}
                      >
                        {risk?.severity || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Probability
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {risk?.probability ?? 'N/A'}%
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Status
                    </div>
                    <div>
                      <span
                        className={`badge badge-sm ${risk?.status === 'Open'
                            ? 'bg-warning'
                            : risk?.status === 'Mitigated'
                              ? 'bg-success'
                              : 'bg-secondary'
                          }`}
                        style={{ fontSize: '11px' }}
                      >
                        {risk?.status || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Risk Score
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {risk?.riskScore ?? 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Impact
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#dc2626',
                        fontWeight: '600',
                      }}
                    >
                      {risk?.impact ?? 'N/A'}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="card h-100">
              <div
                className="card-header py-2"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: '#495057',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}
                >
                  📅 Timeline
                </h6>
              </div>
              <div className="card-body py-3">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Last Assessed
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {formatDate(risk?.lastAssessed)}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Next Assessment
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#212529',
                        fontWeight: '500',
                      }}
                    >
                      {formatDate(risk?.nextAssessment)}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                        marginBottom: '4px',
                      }}
                    >
                      Description
                    </div>
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                      {risk?.description || 'No description available'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskExposure;
